import { useId } from "react";
import { Select } from "@/components/atoms";
import { useCmsFormSaving } from "@/components/organisms/CmsForm";
import type { CmsFieldBaseProps } from "./CmsFieldBase";
import { CmsFieldShell } from "./CmsFieldShell";
import "./CmsField.scss";

interface CmsSelectFieldProps extends CmsFieldBaseProps {
  label: string;
  options: Record<string, string>;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  variant?: "primary" | "secondary";
  density?: "compact" | "default" | "spacious";
  motionScale?: "sm" | "base" | "lg";
  disabled?: boolean;
  triggerClassName?: string;
}

/** CMS form field wrapping a Select control. */
export function CmsSelectField({
  label,
  id,
  hint,
  error,
  requiredMark,
  options,
  value,
  onChange,
  placeholder,
  variant = "secondary",
  density = "default",
  motionScale = "base",
  disabled,
  className,
  colSpan,
  triggerClassName,
}: CmsSelectFieldProps) {
  const isFormSaving = useCmsFormSaving();
  const isDisabled = disabled || isFormSaving;
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const errorId = error ? `${fieldId}-error` : undefined;

  return (
    <CmsFieldShell
      label={label}
      id={fieldId}
      labelFor={fieldId}
      hint={hint}
      error={error}
      requiredMark={requiredMark}
      className={className}
      colSpan={colSpan}
    >
      <Select
        id={fieldId}
        aria-describedby={errorId}
        options={options}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        variant={variant}
        density={density}
        motionScale={motionScale}
        fullWidth
        disabled={isDisabled}
        className={["cms-field__input", triggerClassName].filter(Boolean).join(" ")}
      />
    </CmsFieldShell>
  );
}
